import classNames from 'classnames'
import type { FunctionComponent } from 'react'

import { Box } from '../../components/box'
import { Heading } from '../../components/heading'
import { Image } from '../../components/image'
import { Section } from '../../components/layout/section'
import { SectionBlock } from '../../components/layout/section-block'
import { Markdown } from '../../components/markdown'
import { Paragraph } from '../../components/paragraph'
import { Row } from '../../components/row'
import type { StepFragment } from '../../graphql/fragments/StepFragment.graphql'
import { useSectionOfSteps } from '../../graphql/hooks/section-of-steps'
import { EMPTY_COLLECTION, EMPTY_SECTION } from '../../graphql/placeholders'
import type { StepsFragment } from '../../graphql/queries/GetSteps.graphql'
import { usePersonalizations } from '../../lib/hooks/usePersonalizations'
import { personalizeStepsSection } from '../../shared/util/personalization'
import type { EntryMeta } from '../../types/sys'
import { ActionButton } from '../action-button'
import styles from './section-of-steps.module.scss'

export const SectionOfSteps: FunctionComponent<StepsFragment> = ({
    sys,
    type,
    ...restOfProps
}) => {
    if (restOfProps?.steps) {
        return <SectionOfStepsResolved {...restOfProps} sys={sys} type={type} />
    }
    return <SelfFetchingSectionOfSteps sys={sys} type={type} />
}

const SelfFetchingSectionOfSteps: FunctionComponent<EntryMeta> = ({ sys }) => {
    const { section, fetching } = useSectionOfSteps(sys.id)

    return <SectionOfStepsResolved {...section} loading={fetching} />
}

/**
 * Displays a row of steps with data fetched from Contentful.
 */
const SectionOfStepsResolved: FunctionComponent<
    StepsFragment & { loading?: boolean }
> = (props) => {
    const personalizations = usePersonalizations()
    const loading = props.loading ?? false
    const {
        section: { title, subtitle, anchor } = EMPTY_SECTION,
        steps: { items } = EMPTY_COLLECTION,
        action,
        centered,
    } = personalizeStepsSection(props, personalizations)

    // only show icons if all steps have icons
    const showIcons = items.every((e) => e.icon)
    const tag = showIcons ? 'ul' : 'ol'

    // TODO: step component
    const renderStep = (
        { sys, icon, title, subtitle }: StepFragment,
        index: number
    ) => {
        const content = (
            <>
                {showIcons && icon?.source && (
                    <Image
                        className={styles.icon}
                        source={icon.source}
                        source2x={icon.source2x}
                        source3x={icon.source3x}
                        alt={icon.alt}
                    />
                )}

                {title && <Heading level={3}>{title}</Heading>}
                {subtitle && (
                    <Paragraph className={styles.description}>
                        {subtitle}
                    </Paragraph>
                )}
            </>
        )
        const className = classNames(styles.item, {
            [styles['item-ordered']]: !showIcons,
            [styles[`item-${(index % 4) + 1}`]]: !showIcons,
        })

        return (
            <li key={sys.id} className={className}>
                {content}
            </li>
        )
    }

    return (
        <Section loading={loading} anchor={anchor}>
            <SectionBlock
                textAlignOnLargeScreen={centered ? 'center' : 'left'}
                align='stretch'
            >
                <Box>
                    <header className={styles.header}>
                        {title && (
                            <Markdown
                                tag='h2'
                                typeStyle={{
                                    _: 'heading-lg',
                                }}
                                markdown={title}
                            />
                        )}
                        {subtitle && <Paragraph>{subtitle}</Paragraph>}
                    </header>

                    <Row
                        tag={tag}
                        className={classNames(styles.list, {
                            [styles['list-ordered']]: !showIcons,
                        })}
                    >
                        {loading
                            ? Array.from(Array(4), (_, index) => (
                                  <StepSkeleton
                                      key={index}
                                      ordered={!showIcons}
                                  />
                              ))
                            : items.map(renderStep)}
                    </Row>

                    {action && (
                        <div className={styles.action}>
                            <ActionButton
                                {...action}
                                containerElementId={anchor}
                                placement='section'
                            />
                        </div>
                    )}
                </Box>
            </SectionBlock>
        </Section>
    )
}

const StepSkeleton: FunctionComponent<{ ordered: boolean }> = ({ ordered }) => (
    <li
        className={classNames(styles.item, styles['item-skeleton'], {
            [styles['item-ordered']]: ordered,
        })}
    >
        <Paragraph loading />
    </li>
)

export default SectionOfSteps
